import { getRuntimeConfig } from "../Config/RuntimeConfig";

// GA4 Web data stream Measurement ID for the standalone "ER-booking-website"
// property (537010482). Non-secret — it ships in the client bundle.
//
// NOTE: This module uses gtag.js directly (NOT the Firebase JS SDK). The target
// is a standalone GA4 web property, not a Firebase project, so gtag.js is the
// correct transport. The filename is kept for import stability.
// For QA/DebugView, use the GA Debugger Chrome extension (no in-app debug flag).
const MEASUREMENT_ID = "G-JK7NQL1JQH";
const GTAG_SRC = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;

type GtagFn = (...args: unknown[]) => void;

let _ready = false;
let _initPromise: Promise<boolean> | null = null;

/**
 * Initialize GA4 (gtag.js) for the ER-booking-website property. Idempotent.
 * MUST only be called after the user has granted `statistic` consent (callers
 * enforce this). Returns false when disabled by the runtime flag or on the
 * server (SSR). `send_page_view:false` keeps only our explicit P0 events.
 */
export async function initAnalytics(): Promise<boolean> {
  if (_ready) return true;
  if (_initPromise) return _initPromise;

  _initPromise = (async () => {
    try {
      if (typeof window === "undefined" || typeof document === "undefined") {
        return false; // SSR / non-browser
      }
      const config = await getRuntimeConfig();
      if (config?.NEXT_PUBLIC_ENABLE_ANALYTICS !== "true") return false;

      const w = window as unknown as { dataLayer?: unknown[]; gtag?: GtagFn };
      w.dataLayer = w.dataLayer || [];
      if (!w.gtag) {
        // Canonical gtag shim — pushes the raw `arguments` object to dataLayer.
        w.gtag = function gtag() {
          // eslint-disable-next-line prefer-rest-params
          (w.dataLayer as unknown[]).push(arguments);
        } as unknown as GtagFn;
      }

      // Load the gtag.js library (async; queued commands flush once it loads).
      if (!document.querySelector(`script[src="${GTAG_SRC}"]`)) {
        const script = document.createElement("script");
        script.async = true;
        script.src = GTAG_SRC;
        document.head.appendChild(script);
      }

      w.gtag("js", new Date());
      w.gtag("config", MEASUREMENT_ID, { send_page_view: false });

      _ready = true;
      return true;
    } catch (error) {
      console.error("Analytics init failed:", error);
      return false;
    } finally {
      // Allow a future retry only if init did not complete.
      if (!_ready) _initPromise = null;
    }
  })();

  return _initPromise;
}

/** The gtag function once initialized (after consent); null otherwise. */
export function analytics(): GtagFn | null {
  if (!_ready || typeof window === "undefined") return null;
  return (window as unknown as { gtag?: GtagFn }).gtag ?? null;
}
